import { useTranslation } from 'react-i18next'
import { decodeTinting, DecodedTinting } from '@ca/common'
import CollapsibleSectionRow from '@ui/elements/SectionRow/CollapsibleSectionRow'
import Row, { TextItem } from '@ui/elements/SectionRow/NestedRow'
import { getVariableIcon } from '@ui/elements/Icon/maps'
import AttributeIntensity from '@order/steps/attributes/Intensity/Intensity'
import { TintingIntensity } from '@order/steps/types'
import { useColors } from 'hooks/useColor'
import { SectionProps } from '../types'
import { StyleProp, TextStyle, View } from 'react-native'

type Props = SectionProps

const style: StyleProp<TextStyle> = { textTransform: 'uppercase' }

const toIntensityValue = (
  intensity: DecodedTinting['intensity']
): TintingIntensity => {
  if (intensity.kind === 'single') {
    return `${intensity.value}`
  }
  if (intensity.kind === 'gradient') {
    return `${intensity.from}-${intensity.to}`
  }
  return 'full'
}

export default function ConfigurationSection({ data }: Props) {
  const { t } = useTranslation(['order', 'frame', 'steps'])
  const header = 'order:details.sections.configuration.header'
  const label = 'order:details.labels'

  const config = data.config
  const colors = useColors()

  const tinting = decodeTinting(config.tinting, colors)
  const tintingText = tinting
    ? [
        tinting.colorNameKey ? t(tinting.colorNameKey) : tinting.colorCode,
        tinting.intensityLabel,
      ]
        .filter(Boolean)
        .join(' ')
    : undefined

  return (
    <CollapsibleSectionRow
      icon="settings"
      title={t(`${header}.title`)}
      desc={t(`${header}.desc`)}
    >
      <Row
        icon={getVariableIcon('deal')}
        title={t(`${label}.strategy`)}
        texts={[{ style, text: config.strategy_ref }]}
      />
      <Row
        icon={getVariableIcon('lens')}
        title={t(`${label}.lens`)}
        texts={[{ style, text: config.lens }]}
      />
      <Row
        icon={getVariableIcon('lifestyle')}
        title={t(`${label}.lifestyle`)}
        texts={[{ style, text: config.lifestyle }]}
      />
      <Row
        icon={getVariableIcon('distance')}
        title={t(`${label}.distance`)}
        texts={[{ style, text: config.distance }]}
      />
      <Row
        icon={getVariableIcon('intensity')}
        title={t(`${label}.tinting`)}
        body={
          <View className="flex flex-row items-center gap-5">
            <TextItem
              value={tintingText ? { style, text: tintingText } : undefined}
              index={0}
            />
            {tinting?.color && (
              <AttributeIntensity
                colorConfig={tinting.color}
                intensity={toIntensityValue(tinting.intensity)}
                size={24}
              />
            )}
          </View>
        }
      />
      <Row
        icon={getVariableIcon('corridor')}
        title={t(`${label}.corridor`)}
        texts={[config.corridor]}
      />
      <Row
        icon={getVariableIcon('mirror')}
        title={t(`${label}.mirror`)}
        texts={[config.mirror]}
      />
      <Row
        last
        icon={getVariableIcon('insurance')}
        title={t(`${label}.insurance`)}
        bool={config.premium_insurance}
      />
    </CollapsibleSectionRow>
  )
}
